import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const unreadChannelSchema = z.object({
  channel_id: z.string().min(1),
  channel_name: z.string(),
  unread_count: z.number().int(),
  mention_count: z.number().int(),
});

export const unreadSuccessSchema = z.object({
  channels: z.array(unreadChannelSchema),
  total_unread: z.number().int(),
  total_mentions: z.number().int(),
});

export type UnreadChannel = z.infer<typeof unreadChannelSchema>;
export type UnreadSuccess = z.infer<typeof unreadSuccessSchema>;

export function unreadPath(workspaceId: string): string {
  return `/api/unread?workspace_id=${encodeURIComponent(workspaceId)}`;
}

export async function unreadRequest({
  apiBaseUrl,
  accessToken,
  workspaceId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  workspaceId: string;
  fetchImpl?: FetchLike;
}): Promise<UnreadSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${unreadPath(workspaceId)}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const msg =
      payload && typeof payload === "object" && "error" in payload
        ? (payload as { error: string }).error
        : "Failed to get unread counts";
    throw new SharedApiError(msg, response.status);
  }

  const payload = await response.json().catch(() => null);
  const parsed = unreadSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid unread response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
